
import { differenceInDays } from "date-fns";

/**
 * Calculate days between dates (inclusive of both start and end dates)
 * Used for fishing permit day calculations
 */
export const calculateDays = (startDate: Date | undefined, endDate: Date | undefined): number => {
  if (!startDate || !endDate) return 0;
  
  // Normalize dates to midnight for consistent comparison
  const normalizedStart = new Date(startDate);
  normalizedStart.setHours(0, 0, 0, 0);
  
  const normalizedEnd = new Date(endDate);
  normalizedEnd.setHours(0, 0, 0, 0);
  
  // Add 1 because we want to include both the start and end date
  const days = differenceInDays(normalizedEnd, normalizedStart) + 1;
  console.log(`[permitUtils] calculateDays: ${normalizedStart.toDateString()} to ${normalizedEnd.toDateString()} = ${days} days (inclusive)`);
  return days;
};

/**
 * Calculate nights between dates (end date - start date)
 * Used for camping night calculations
 */
export const calculateNights = (startDate: Date | undefined, endDate: Date | undefined): number => {
  if (!startDate || !endDate) return 0;
  
  // Normalize dates to midnight for consistent comparison
  const normalizedStart = new Date(startDate);
  normalizedStart.setHours(0, 0, 0, 0);
  
  const normalizedEnd = new Date(endDate);
  normalizedEnd.setHours(0, 0, 0, 0);
  
  // Nights = gaps between days (hotel-style: arrive Apr 10, depart Apr 12 = 2 nights)
  const nights = differenceInDays(normalizedEnd, normalizedStart);
  console.log(`[permitUtils] calculateNights: ${normalizedStart.toDateString()} to ${normalizedEnd.toDateString()} = ${nights} nights (exclusive)`);
  return nights;
};

/**
 * Calculate fishing permit price based on permit type and days
 */
export const calculateFishingPrice = (
  permitName: string, 
  days: number, 
  basePrice: number
): number => {
  // For daily permits, multiply by days
  if (permitName.startsWith("Daily") || permitName === "All Fisheries (Ages 14+)" || permitName === "Two Poles (Ages 14+)") {
    const dailyRate = (permitName === "Two Poles (Ages 14+)" || 
                      permitName.toLowerCase().includes("two poles") || 
                      permitName.includes("2 poles")) ? 40 : 25;
    const fishingPrice = dailyRate * days;
    console.log(`[permitUtils] calculateFishingPrice: ${permitName} - ${dailyRate} × ${days} days = ${fishingPrice}`);
    return fishingPrice;
  } 
  // For annual permits, use base price
  else {
    console.log(`[permitUtils] calculateFishingPrice: Annual permit ${permitName} - ${basePrice}`);
    return basePrice;
  }
};

/**
 * Calculate camping price based on nights
 */
export const calculateCampingPrice = (nights: number): number => {
  const campingRate = 15; // $15 per night
  
  // When nights is 0 but we're camping the same day, charge for 1 night
  const effectiveNights = nights === 0 ? 1 : nights;
  
  const total = campingRate * effectiveNights;
  console.log(`[permitUtils] calculateCampingPrice: ${effectiveNights} nights @ $${campingRate} = $${total}`);
  return total;
};

/**
 * Calculate total price for a permit including fishing and optional camping
 * NOTE: This does NOT include processing fees - those are added at checkout
 */
export const calculateTotalPrice = (
  permitName: string,
  days: number,
  basePrice: number,
  includesCamping: boolean,
  campingNights: number
): number => {
  const fishingPrice = calculateFishingPrice(permitName, days, basePrice);
  let campingPrice = 0;
  
  if (includesCamping) {
    // When nights is 0 but we're camping the same day, charge for 1 night
    const effectiveNights = campingNights === 0 ? 1 : campingNights;
    campingPrice = calculateCampingPrice(effectiveNights);
  }
  
  const subtotal = fishingPrice + campingPrice;
  
  console.log(`[permitUtils] calculateTotalPrice:
    - Permit: ${permitName}
    - Fishing: $${fishingPrice} (${days} days)
    - Camping: $${campingPrice} (${includesCamping ? (campingNights === 0 ? '1' : campingNights) : 0} nights)
    - Subtotal (before processing): $${subtotal}
  `);
  
  return subtotal;
};

/**
 * Get daily rate for fishing permits
 */
export const getFishingDailyRate = (permitName: string): number => {
  return (permitName === "Two Poles (Ages 14+)" || 
          permitName.toLowerCase().includes("two poles") || 
          permitName.includes("2 poles")) ? 40 : 25;
};

/**
 * Get processing fee based on total amount (before processing fee)
 */
export const getProcessingFee = (amount: number): number => {
  if (amount <= 49) return 3;
  else if (amount <= 110) return 5;
  else if (amount <= 187) return 7;
  else if (amount <= 299) return 10;
  else if (amount <= 440) return 15;
  else if (amount <= 600) return 20;
  else if (amount <= 800) return 30;
  else return 35;
};

/**
 * Calculate total price including processing fee
 */
export const calculateTotalWithProcessingFee = (
  permitName: string,
  days: number,
  basePrice: number,
  includesCamping: boolean,
  campingNights: number
): number => {
  const subtotal = calculateTotalPrice(permitName, days, basePrice, includesCamping, campingNights);
  const processingFee = getProcessingFee(subtotal);
  const total = subtotal + processingFee;
  
  console.log(`[permitUtils] calculateTotalWithProcessingFee:
    - Subtotal: $${subtotal}
    - Processing Fee: $${processingFee}
    - Total: $${total}
  `);
  
  return total;
};
